import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Microscope, ChevronDown, Pencil, Trash2, MapPin } from "lucide-react";
import { formatExamDate, formatRiskBadge } from "@/lib/formatters/examFormatter";
import { useState } from "react";

interface AnatomopathologyCardProps {
  record: any;
  onUpdate: (updated: any) => void;
  onDelete: () => void;
}

export function AnatomopathologyCard({ record, onUpdate, onDelete }: AnatomopathologyCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const riskBadge = formatRiskBadge(record.risk_level || 'baixo');

  const formattedDate = record.event_date
    ? formatExamDate(record.event_date)
    : 'Data não especificada';

  return (
    <Card className="overflow-hidden border-l-4 border-l-primary">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Microscope className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base leading-tight mb-1">
                {record.exam_type || 'Exame Histopatológico'}
              </h3>
              {record.specimen_type && (
                <p className="text-sm text-muted-foreground">
                  {record.specimen_type}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs text-muted-foreground">
                  {formattedDate}
                </span>
                <Badge variant={riskBadge.variant} className="text-xs">
                  {riskBadge.emoji} {riskBadge.label}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onUpdate(record)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        {/* Material Examinado */}
        {record.specimen_sites && record.specimen_sites.length > 0 && (
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-start gap-2 mb-2">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <h4 className="font-medium text-sm">Material Examinado</h4>
            </div>
            <div className="flex flex-wrap gap-1.5 ml-6">
              {record.specimen_sites.map((site: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {site}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Diagnósticos */}
        {record.histopathological_diagnoses && record.histopathological_diagnoses.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <span className="text-primary">✓</span> Diagnósticos Histopatológicos
            </h4>
            <div className="space-y-3">
              {record.histopathological_diagnoses.map((diag: any, idx: number) => (
                <div key={idx} className="pl-4 border-l-2 border-primary/30">
                  {diag.site && (
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      {diag.site}
                    </p>
                  )}
                  <p className="text-sm font-medium mb-1">
                    {diag.diagnosis}
                  </p>
                  {diag.details && (
                    <p className="text-xs text-muted-foreground">
                      {diag.details}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Exame Macroscópico (Colapsável) */}
        {record.macroscopic_exam && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              Ver descrição macroscópica
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="bg-muted/30 rounded p-3 text-xs text-muted-foreground whitespace-pre-wrap">
                {record.macroscopic_exam}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Médico Patologista */}
        {(record.doctor_name || record.doctor_crm) && (
          <div className="pt-2 border-t flex items-center gap-2 text-xs text-muted-foreground">
            <span>👨‍⚕️</span>
            <span>
              {record.doctor_name}
              {record.doctor_crm && ` - CRM ${record.doctor_crm}`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
